import { Card, CardContent, Grid, Typography, Box, Chip } from '@mui/material';
import type { ArtistMetrics } from '../../hooks/useArtistMetrics';
import type { EventData } from '../utils/revenueProjections';

interface ArtistSnapshotCardProps {
    metrics: ArtistMetrics;
    events: EventData[];
    editorialPlaylistCount: number;
}

const fmt = (val: number | null | undefined): string => {
    if (val == null) return '—';
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val.toLocaleString();
};

const pct = (val: number | null | undefined): string => {
    if (val == null) return '—';
    return `${val.toFixed(2)}%`;
};

const trendColor = (trend: string | null): string => {
    if (trend === 'up') return '#4caf50';
    if (trend === 'down') return '#f44336';
    return '#ff9800';
};

export const ArtistSnapshotCard = ({
    metrics,
    events,
    editorialPlaylistCount,
}: ArtistSnapshotCardProps) => {
    const avgCapacity =
        events
            .map((e) => e.venue_capacity)
            .filter((c): c is number => c != null && c > 0)
            .reduce((a, b, _, arr) => a + b / arr.length, 0) || 0;

    const prices = events
        .map((e) => {
            const low = e.ticket_price_low ?? 0;
            const high = e.ticket_price_high ?? 0;
            return low > 0 && high > 0 ? `$${low}-$${high}` : low > 0 ? `$${low}` : high > 0 ? `$${high}` : null;
        })
        .filter(Boolean);
    const avgTicketRange = prices.length ? prices[0] : '—';

    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Artist Snapshot</Typography>
                    <Box display="flex" gap={1}>
                        <Chip label={metrics.career_stage} size="small" color="primary" variant="outlined" />
                        {metrics.growth_trend && (
                            <Chip
                                label={metrics.growth_trend === 'up' ? 'Growing' : metrics.growth_trend === 'down' ? 'Declining' : 'Stable'}
                                size="small"
                                sx={{ backgroundColor: trendColor(metrics.growth_trend), color: '#fff' }}
                            />
                        )}
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Spotify */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Spotify
                        </Typography>
                        <MetricRow label="Monthly Listeners" value={fmt(metrics.spotify_monthly_listeners)} />
                        <MetricRow label="Followers" value={fmt(metrics.spotify_followers)} />
                        <MetricRow label="Playlist Reach" value={fmt(metrics.spotify_playlist_reach)} />
                        <MetricRow label="Playlist Count" value={fmt(metrics.spotify_playlist_count)} />
                    </Grid>

                    {/* Social */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Social
                        </Typography>
                        <MetricRow label="Instagram" value={fmt(metrics.instagram_followers)} />
                        <MetricRow label="IG Engagement" value={pct(metrics.instagram_engagement_rate)} />
                        <MetricRow label="TikTok" value={fmt(metrics.tiktok_followers)} />
                        <MetricRow label="YouTube" value={fmt(metrics.youtube_subscribers)} />
                    </Grid>

                    {/* Live */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Live
                        </Typography>
                        <MetricRow label="Upcoming Events" value={String(events.length)} />
                        <MetricRow label="Avg Venue Cap" value={avgCapacity > 0 ? fmt(avgCapacity) : '—'} />
                        <MetricRow label="Ticket Range" value={avgTicketRange ?? '—'} />
                    </Grid>

                    {/* Other */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Other
                        </Typography>
                        <MetricRow label="Editorial Playlists" value={String(editorialPlaylistCount)} />
                        <MetricRow label="Shazam" value={fmt(metrics.shazam_count)} />
                        <MetricRow label="CM Score" value={metrics.cm_artist_score?.toLocaleString() ?? '—'} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const MetricRow = ({ label, value }: { label: string; value: string }) => (
    <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2" color="textSecondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold">
            {value}
        </Typography>
    </Box>
);
